import { Country } from '@/shared/domain/entities/country.entity'
import { JobSalaryCurrency } from '@/shared/domain/enums/job/job-salary-currency.enum'
import { JobSearchLocation } from '@/types/job'

interface FullNameProps {
  firstname?: string | null
  lastname?: string | null
}

export const getFullName = <T extends FullNameProps>({ firstname, lastname }: T): string => {
  return `${firstname ?? ''} ${lastname ?? ''}`
}

export const getFullNameInitial = <T extends FullNameProps>({ firstname, lastname }: T): string => {
  const firstnameInitials = firstname
    ? firstname
        .split(' ')
        .map((f) => f[0])
        .join('')
    : ''

  const lastnameInitials = lastname
    ? lastname
        .split(' ')
        .map((l) => l[0])
        .join('')
    : ''

  return `${firstnameInitials}${lastnameInitials}`
}

interface JobNameProps {
  currentCompany: string | null
  currentJob: string | null
}

export const getJobName = <T extends JobNameProps>({ currentCompany, currentJob }: T): string => {
  if (!currentJob && !currentCompany) return ''
  return `${currentJob || '-'} ${currentCompany ? `@ ${currentCompany}` : ''}`
}

export interface ApplicantLocationProps {
  city?: string
  country: Country | null
}

export function formatApplicantLocation<T extends ApplicantLocationProps>({ city, country }: T): string {
  if (!city && !country) return ''
  return `${city || ''}${city && country?.name ? ', ' : ''}${country?.name || ''}`
}

export function getUniqueLocations(jobLocations: JobSearchLocation[]): string[] {
  const locations = jobLocations.map((location) => location.location.name)
  return [...new Set(locations)]
}

export function formatJobLocations(jobLocations: JobSearchLocation[]): string {
  return getUniqueLocations(jobLocations).join(', ')
}

export function replaceCurlyBracesWithAtSymbol(text: string) {
  return text.replace(/{(.*?)}/g, (_match, variable) => `@${variable}`)
}

export function replaceAtSymbolWithCurlyBraces(text: string) {
  return text.replace(/(?<!\w)@(\w+)/g, (_match, variable) => `{${variable}}`)
}

export function convertVariablesToMentionHtml(text: string) {
  return text.replace(
    /({(\w+)})(?![^<>]*>[^<]*<\/a>)/g,
    '<span data-type="mention" data-label="$2" data-id="$2"></span>',
  )
}

export function extractExcludedVariables(inputHTML: string, stringArray?: string[]) {
  const dataIdRegex = /data-id="([A-Za-z_]+)"/g
  const dataIds = []
  let match

  while ((match = dataIdRegex.exec(inputHTML)) !== null) {
    dataIds.push(match[1])
  }

  const filteredVariables = dataIds.filter((id) => !stringArray?.includes(id))

  return filteredVariables.map((variable) => `@${variable}`)
}

export function replaceRecruiterMentions(text: string, recruiters: { label: string; id: string }[]): string {
  return text.replace(/\{ recruiterId:'(.*?)' \}/g, (match, recruiterId) => {
    const recruiter = recruiters.find((r) => r.id === recruiterId)
    if (!recruiter) return match
    return `<span data-type="mention" data-label="${recruiter.label}" data-id="${recruiter.id}"></span>`
  })
}

export function replaceMentionsWithRecruiterIds(htmlString: string) {
  const parser = new DOMParser()
  const doc = parser.parseFromString(htmlString, 'text/html')

  const spans = doc.querySelectorAll('span[data-type="mention"]')

  spans.forEach((span) => {
    const id = span.getAttribute('data-id')
    if (id) {
      const replacement = document.createTextNode(`{ recruiterId:'${id}' }`)
      span.replaceWith(replacement)
    }
  })

  return doc.body.innerHTML
}

export function convertMentionHtmlToVariables(text?: string) {
  if (!text) return ''
  return text.replace(/<span[^>]*data-id="([^"]+)"[^>]*>[^<]*<\/span>/g, '{$1}')
}

export function removeHTMLTags(text: string) {
  return text.replace(/<[^>]*>/g, '')
}

export function capitalizeFirstLetter(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase()
}

export const formatSalary = (salary: number | null, currency: JobSalaryCurrency | null) => {
  if (!salary) {
    return '-'
  }

  const formatter = new Intl.NumberFormat('fr-FR', {
    currency: currency || 'EUR',
    minimumFractionDigits: 0,
    style: currency ? 'currency' : 'decimal',
  })
  return formatter.format(salary)
}

export function pluralize(singularText: string, number: number) {
  if (number > 1) {
    return `${number} ${singularText}s`
  } else {
    return `${number} ${singularText}`
  }
}
